// Ownership rules for a partner-marketplace app (DMC). Every function here
// takes the app's own `isOwnProduct` predicate (normally
// `useTripDesignerConfig().isOwnProduct`) rather than an organization id
// directly — an app with no ownership concept (TO) uses the config's default
// `() => true` and every day comes back editable.
type IsOwnProduct = (product: any) => boolean;

/**
 * Whether the experience detail may be opened: own experiences, plus partner
 * transitional programs — those are shared between partners with full
 * access, so their detail stays reachable.
 */
export function canOpenExperienceDetail(
  experience: any,
  isOwnProduct: IsOwnProduct,
): boolean {
  return isOwnProduct(experience) || Boolean(experience?.isTransitionalProgram);
}

/**
 * Whether the current app may edit a given day. Day ownership is carried by
 * the experience placed on it: hotels can legitimately come from a partner
 * marketplace on one's own days, so they never lock a day. Days without an
 * experience remain editable.
 */
export function isDayEditable(
  day: any,
  experiencesMap: Record<string, any> | undefined,
  isOwnProduct: IsOwnProduct,
): boolean {
  const experience = day?.experienceId
    ? experiencesMap?.[day.experienceId]
    : null;

  return Boolean(experience?.isTransitionalProgram) || isOwnProduct(experience);
}

/**
 * Whether the current app may mutate a hotel placed on a day: only when it
 * owns the hotel product itself. Distinct from `isDayEditable` — hotels can
 * legitimately come from a partner marketplace on one's own day, so day
 * ownership alone does not grant hotel mutation rights.
 */
export function canMutateHotelForDay(
  hotel: any,
  isOwnProduct: IsOwnProduct,
): boolean {
  return isOwnProduct(hotel);
}

/**
 * Whether the current app may remove a hotel from a day. Looser than
 * mutation: on the app's own days (day ownership carried by the experience,
 * or a transitional program) a partner hotel may still be removed so it can
 * be replaced with the app's own accommodation.
 */
export function canRemoveHotelForDay(
  hotel: any,
  experience: any,
  isOwnProduct: IsOwnProduct,
): boolean {
  return (
    canMutateHotelForDay(hotel, isOwnProduct) ||
    isOwnProduct(experience) ||
    Boolean(experience?.isTransitionalProgram)
  );
}

/**
 * Whether any day at or after startIndex is not editable by the current app.
 * Inserting or removing a day shifts the dates of every day that follows the
 * edit point without touching those days themselves — callers use this to
 * block an edit that would silently move a partner's day to a different date.
 */
export function hasPartnerDayInRange(
  days: any[],
  experiencesMap: Record<string, any> | undefined,
  isOwnProduct: IsOwnProduct,
  startIndex: number,
): boolean {
  for (let i = Math.max(startIndex, 0); i < days.length; i++) {
    if (!isDayEditable(days[i], experiencesMap, isOwnProduct)) {
      return true;
    }
  }
  return false;
}
